import BenchlingConfig from 'BenchlingConfig';
import {
  isAgentsFeatureEnabled,
  isEventBasedAgentsEnabled,
  isTimeBasedAgentsEnabled,
} from 'llm/isAgentsFeatureEnabled';
import type {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

export interface ChatUseCaseTenantContext {
  isAcademicTenant: boolean;
}

/**
 * Capabilities this module cannot read for itself, supplied by the caller.
 *
 * Everything else is gated on {@link BenchlingConfig}, which is a leaf module. These come from
 * LaunchDarkly, which reaches redux and analytics, and analytics imports this catalog — so reading
 * them here would make that a cycle. The caller reads the flag; the catalog still decides what it
 * means for each category.
 */
export interface ChatUseCaseCapabilities {
  /**
   * Whether the LLM_ENABLE_MANAGE_ROUTINE_TOOL rollout is on. The Assistant's ManageAgent tool
   * needs it as well as a trigger type (see `is_manage_routine_tool_enabled`), so outside the
   * rollout it has no tool to set an agent up with. Only the rollout is passed in; the trigger
   * types are tenant config this module reads for itself.
   */
  isManageRoutineToolEnabled: boolean;
}

/** A prompt that is either fixed or resolved per tenant; `null` hides the use case in that tenant. */
export type ChatUseCasePrompt = string | ((context: ChatUseCaseTenantContext) => string | null);

/** Raw use case definition; resolved at read time via {@link getChatUseCaseCategories}. */
export interface ChatUseCaseDefinition {
  /**
   * Globally unique across every category, including disabled ones. This is the persistence key for
   * "already tried" (see useCaseUsage), so renaming an id silently resets that history for every
   * user, and ids must never be derived from labels. Moving a use case between categories is safe.
   */
  id: string;
  label: string | ((context: ChatUseCaseTenantContext) => string);
  prompt: ChatUseCasePrompt;
  /**
   * Whether in-product surfaces (the Use cases tab, chat-box pills, onboarding, homepage) list this
   * use case. URL lookup ignores this, so a marketing CTA can still open a prompt those surfaces
   * never show. Product use cases set this true; campaign-only prompts set it false.
   */
  featured: boolean;
  /**
   * Hides a single use case for tenants without the capability its prompt assumes, where the
   * category as a whole still applies.
   */
  isEnabled?: () => boolean;
  /**
   * ISO date (YYYY-MM-DD) from which to announce this use case, which badges it as new for
   * {@link CHAT_USE_CASE_NEW_FOR_DAYS} afterwards. Omit it for a use case that should never be announced
   * as new.
   */
  addedAt?: string;
}

/** Tenant-resolved use case surfaced to the UI. */
export interface ChatUseCase {
  id: string;
  label: string;
  prompt: string;
  featured: boolean;
  addedAt?: string;
}

/** How long a newly added use case is badged as new. */
export const CHAT_USE_CASE_NEW_FOR_DAYS = 30;

/**
 * The date agent use cases began being announced. Announcement is reserved for the one capability
 * being promoted rather than given to everything recently added, which is why most entries carry no
 * date at all: badging them all would leave nothing standing out.
 */
const AGENT_USE_CASES_ANNOUNCED_AT = '2026-09-15';

/**
 * Whether a chat started now could produce a canvas artifact.
 *
 * The canvas tools are gated on ENABLE_CANVAS_CORE (see `is_canvas_tools_enabled`), which is forced
 * off for GxP tenants, and they are only added to the Assistant's toolkit. Assistant is enabled
 * independently of the other agent types, so with it off `getDefaultAgentType` hands a use case to
 * an agent that has no canvas tools regardless of the canvas config.
 */
function canAssistantCreateArtifact(): boolean {
  return BenchlingConfig.LLM_ENABLE_ASSISTANT_AGENT && BenchlingConfig.ENABLE_CANVAS_CORE;
}

export interface ChatUseCaseCategoryDefinition {
  id: string;
  label: string;
  /** Use the outline (Font Awesome regular) variant so every category glyph has matching weight. */
  icon: BenchlingIconName;
  /** Hides a whole category for tenants without the capability its prompts assume. */
  isEnabled?: (capabilities: ChatUseCaseCapabilities) => boolean;
  chatUseCases: Array<ChatUseCaseDefinition>;
}

export interface ChatUseCaseCategory {
  id: string;
  label: string;
  icon: BenchlingIconName;
  chatUseCases: Array<ChatUseCase>;
}

/**
 * Every use case Benchling AI ships, in the order the Use cases tab lists them.
 *
 * This is the single owner of the data: the Use cases tab reads all of it, and the
 * progressive-disclosure surfaces (chat-box pills, onboarding, homepage) read the chat-workflow
 * subset through ProgressiveDisclosureData. Both share one flat id space, so a use case run from one
 * surface counts as tried on the other.
 */
export const CHAT_USE_CASE_CATEGORIES: Array<ChatUseCaseCategoryDefinition> = [
  {
    id: 'experiment-design',
    label: 'Design experiments',
    icon: 'flask-outline',
    chatUseCases: [
      {
        id: 'design-screening-experiment',
        featured: true,
        label: 'Design a screening experiment',
        prompt:
          'I want to design a structured experiment using a DoE approach. Please ask me one or two questions to determine what factors and responses I want to test, then draft an experimental design.',
      },
      {
        id: 'optimize-assay-process',
        featured: true,
        label: 'Optimize an assay or process',
        prompt: ({isAcademicTenant}) =>
          `I want to optimize an assay or process by varying several factors${isAcademicTenant ? '' : ' at once'}. Please ask me one or two questions about my factors, their ranges, and how many runs I can fit, then draft an experimental design that finds the best conditions efficiently.`,
      },
      {
        id: 'design-custom-experiment',
        featured: true,
        label: 'Design a custom experiment',
        prompt:
          'I want to design an experiment. Please ask me one or two questions to understand my hypothesis and key variables, then draft a structured experimental plan.',
      },
    ],
  },
  {
    id: 'generate-hypotheses',
    label: 'Generate hypotheses',
    icon: 'lightbulb-outline',
    chatUseCases: [
      {
        id: 'investigate-unexpected-result',
        featured: true,
        label: 'Investigate an unexpected result',
        prompt:
          "My results contradict what's reported in the literature. Please ask me one or two questions about my data and the expected mechanism, then reason across my notebook data and the published literature to generate a few plausible hypotheses, ranked by likelihood.",
      },
      {
        id: 'generate-mechanistic-hypotheses',
        featured: true,
        label: 'Generate mechanistic hypotheses',
        prompt:
          "I want to generate hypotheses for why my target or pathway is behaving a certain way. Please ask me one or two questions about my system and observations, then draw on my program's data alongside the literature to draft a ranked list of plausible mechanisms with supporting rationale.",
      },
      {
        id: 'stress-test-hypothesis',
        featured: true,
        label: 'Stress-test my hypothesis',
        prompt:
          'I have an initial hypothesis I want to pressure-test. Please ask me one or two questions about it, then check it against both my own prior experiments and the published literature to suggest alternative or refined hypotheses I may not have considered.',
      },
    ],
  },
  {
    id: 'write-reports',
    label: 'Write reports',
    icon: 'pen-to-square-outline',
    chatUseCases: [
      {
        id: 'experimental-report',
        featured: true,
        label: 'Write a lab report',
        prompt:
          'I want to write a lab report on my recent work. Please ask me one or two questions to determine what the report should be on, and any requirements there are for format, content or style.',
      },
      {
        id: 'regulatory-report',
        featured: true,
        label: 'Write a regulatory report',
        prompt: ({isAcademicTenant}) =>
          isAcademicTenant
            ? null
            : "I want to write a regulatory report on the programs or projects I've been involved in. Please ask me one or two questions to determine the specific type of regulatory report needed, and confirm the scope and topic.",
      },
      {
        id: 'status-report',
        featured: true,
        label: 'Write a status report',
        prompt:
          'I want to write a status report based on my recent work. Offer a suggestion for what this report could contain and ask me one or two questions to confirm the scope of the report and its intended audience.',
      },
      {
        id: 'literature-review-for-paper',
        featured: true,
        label: 'Conduct a literature review for my paper',
        prompt: ({isAcademicTenant}) =>
          isAcademicTenant
            ? "Draft the introduction and background for my paper using my experimental data and relevant literature from the web. Ask me one or two questions to nail down the topic and scope of what's needed, and include citations in the generated draft."
            : null,
      },
    ],
  },
  {
    id: 'capture-data',
    label: 'Capture data',
    icon: 'file-chart-column-outline',
    chatUseCases: [
      {
        id: 'transcribe-handwritten-notes',
        featured: true,
        label: 'Transcribe my handwritten notes',
        prompt: ({isAcademicTenant}) =>
          isAcademicTenant
            ? 'I want to transcribe my handwritten notes. Help me import them into a new notebook entry draft.'
            : null,
      },
      {
        id: 'register-entities',
        featured: true,
        label: 'Register entities from file',
        prompt: ({isAcademicTenant}) =>
          isAcademicTenant
            ? null
            : 'I want to import entities into Benchling. Please ask me one or two questions to determine what entities need to be registered and any context on the data source to assist with registration. Then generate a draft entry with the appropriate registration tables for data capture.',
      },
      {
        id: 'import-results',
        featured: true,
        label: ({isAcademicTenant}) => `Import ${isAcademicTenant ? 'data' : 'results'} from file`,
        prompt: ({isAcademicTenant}) => {
          if (isAcademicTenant) {
            return 'I want to import data into Benchling. Please ask me one or two questions to determine what data needs to be imported and any context on the data source to assist with import. Then generate a draft entry.';
          }
          return 'I want to import results into Benchling. Please ask me one or two questions to determine what results need to be created and any context on the data source to assist with import. Then generate a draft entry with the appropriate result tables for data capture.';
        },
      },
      {
        id: 'create-notebook-entry',
        featured: true,
        label: 'Create and fill a notebook entry',
        prompt: ({isAcademicTenant}) =>
          `I want to draft a notebook entry using AI. Start a draft prefilled with placeholder sections for a standard notebook entry and ask me one or two questions to determine what the purpose of the entry is, ${isAcademicTenant ? 'if it should follow a specific structure.' : 'if it should follow a specific template, and any other specific needs for its structure.'}`,
      },
    ],
  },
  {
    id: 'analyze-data',
    label: 'Analyze data',
    icon: 'magnifying-glass-chart-outline',
    chatUseCases: [
      {
        id: 'statistical-analysis',
        featured: true,
        label: 'Run statistical analysis on data',
        prompt: ({isAcademicTenant}) => {
          const recentData = isAcademicTenant ? 'data' : 'result data';
          return `I want to run statistical analyses on my recent ${recentData}. Please ask me one or two questions to confirm the type of statistical analysis and which data it should be run on.`;
        },
      },
      {
        id: 'chart-results',
        featured: true,
        label: 'Chart and plot results',
        prompt: ({isAcademicTenant}) => {
          const recentData = isAcademicTenant ? 'data' : 'result data';
          return `I want to plot and chart my recent ${recentData}. Please ask me one or two questions to confirm what type of charts or plots are needed and which ${recentData} it should be run on.`;
        },
      },
      {
        id: 'qc-check',
        featured: true,
        label: 'QC check my data',
        prompt:
          "I want to do QC checks on my recently captured data. Please ask me one or two questions to determine which data I'm interested in QC checking and any specific QC requirements I'm trying to compare against.",
      },
      {
        id: 'investigate-audit-trails',
        featured: true,
        label: 'Investigate audit trails',
        isEnabled: () => BenchlingConfig.LLM_ENABLE_AUDIT_LOG_EXPORT_TOOL,
        prompt:
          'I want to find edits that Benchling AI made to entries. Ask me one or two questions to confirm scope (project, template, or time range), then analyze their audit trails and summarize what the AI changed.',
      },
    ],
  },
  {
    id: 'data-visualization',
    label: 'Data visualization',
    icon: 'chart-simple-outline',
    // Both prompts ask the agent to create an artifact, which it cannot do without canvas.
    isEnabled: canAssistantCreateArtifact,
    chatUseCases: [
      {
        id: 'design-progress-dashboard',
        featured: true,
        label: 'Design a dashboard to track project progress',
        prompt:
          'I want to build a dashboard to track project progress. Ask me one or two questions about the project then create an artifact.',
      },
      {
        id: 'vibe-code-dashboard',
        featured: true,
        label: 'Vibe code a dashboard',
        prompt:
          'I want to build a dashboard to visualize my data. Ask me one or two questions about what to capture and how to visualize it, then create an artifact.',
      },
    ],
  },
  {
    id: 'create-an-agent',
    label: 'Create an agent',
    icon: 'robot-outline',
    // These prompts walk the user into agent setup, which the Assistant can only carry out with
    // Agents available and the ManageAgent tool behind them.
    isEnabled: ({isManageRoutineToolEnabled}) => isAgentsFeatureEnabled() && isManageRoutineToolEnabled,
    chatUseCases: [
      // Each prompt below names the schedule or event that triggers the agent, so it is only
      // reachable where that trigger type is enabled; the category gate passes on either one.
      {
        id: 'daily-entry-review',
        featured: true,
        label: 'Conduct daily review of my entries',
        prompt:
          'I want an agent that checks my entries every day and flags incomplete work, inconsistencies, and quality issues. Ask me one or two questions to set up the agent.',
        isEnabled: isTimeBasedAgentsEnabled,
        addedAt: AGENT_USE_CASES_ANNOUNCED_AT,
      },
      {
        id: 'weekly-work-roundup-dashboard',
        featured: true,
        label: 'Create a weekly roundup dashboard of my work',
        prompt:
          "I want a canvas artifact created every week that visualizes what I've accomplished. Ask me one or two questions to set up the agent.",
        // Needs both capabilities: a schedule to run on, and canvas to produce the artifact.
        isEnabled: () => isTimeBasedAgentsEnabled() && canAssistantCreateArtifact(),
        addedAt: AGENT_USE_CASES_ANNOUNCED_AT,
      },
      {
        id: 'auto-generate-workflow-summaries',
        featured: true,
        label: 'Auto generate workflow summaries upon completion',
        prompt:
          'I want an agent that writes a summary every time a workflow task type finishes and saves it to a project. Ask me one or two questions to help set up this agent.',
        isEnabled: isEventBasedAgentsEnabled,
        addedAt: AGENT_USE_CASES_ANNOUNCED_AT,
      },
      {
        id: 'create-custom-agent',
        featured: true,
        label: 'Create a custom agent',
        prompt:
          'I want to create an agent to automate a task based on a schedule or trigger and generate outputs to save in a project. Ask me one or two questions to help set up the agent.',
        addedAt: AGENT_USE_CASES_ANNOUNCED_AT,
      },
    ],
  },
  // Keep "Other" last: it is the catch-all, so it trails whatever the catalog gains. A test pins
  // this, since the array order is what the dropdown and the unfiltered list render.
  {
    id: 'other',
    label: 'Other',
    icon: 'star-outline',
    chatUseCases: [
      {
        id: 'create-new-skill',
        featured: true,
        label: 'Create a new skill',
        prompt:
          'I want to create a new Benchling AI skill. Give me a very brief description of what skills are and ask me one or two questions to guide me through the process of creating one.',
      },
      {
        id: 'create-powerpoint-presentation',
        featured: true,
        label: 'Create a PowerPoint presentation',
        prompt:
          'I want to create a PowerPoint presentation based on my data. Ask me one or two questions to guide me through the process of creating one.',
      },
      // Campaign-only prompts belong in the catalog so a `?use-case=` link can resolve them, but they
      // are not listed in the Use cases tab or the chat-box pills. Copy this shape when adding one:
      // {
      //   id: 'campaign-example',
      //   featured: false,
      //   label: 'Try this from the campaign',
      //   prompt:
      //     'I want to try the workflow this campaign is about. Ask me one or two questions to get started.',
      // },
    ],
  },
];

/**
 * The categories whose prompts drive an ordinary chat workflow, in the order the chat surfaces list
 * them. These back the progressive-disclosure selectors (chat-box pills, onboarding, homepage); the
 * remaining categories are tab-only, so broadening those surfaces means adding ids here.
 */
export const CHAT_WORKFLOW_CATEGORY_IDS: ReadonlyArray<string> = [
  'capture-data',
  'analyze-data',
  'experiment-design',
  'generate-hypotheses',
  'write-reports',
];

function resolvePrompt(prompt: ChatUseCasePrompt, context: ChatUseCaseTenantContext): string | null {
  return typeof prompt === 'function' ? prompt(context) : prompt;
}

function resolveLabel(label: ChatUseCaseDefinition['label'], context: ChatUseCaseTenantContext): string {
  return typeof label === 'function' ? label(context) : label;
}

function resolveUseCase(
  chatUseCase: ChatUseCaseDefinition,
  context: ChatUseCaseTenantContext
): ChatUseCase | null {
  if (!(chatUseCase.isEnabled?.() ?? true)) {
    return null;
  }
  const prompt = resolvePrompt(chatUseCase.prompt, context);
  if (prompt == null) {
    return null;
  }
  return {
    id: chatUseCase.id,
    label: resolveLabel(chatUseCase.label, context),
    prompt,
    featured: chatUseCase.featured,
    addedAt: chatUseCase.addedAt,
  };
}

function resolveCategory(
  category: ChatUseCaseCategoryDefinition,
  context: ChatUseCaseTenantContext
): ChatUseCaseCategory {
  return {
    id: category.id,
    label: category.label,
    icon: category.icon,
    chatUseCases: category.chatUseCases
      .map((chatUseCase) => resolveUseCase(chatUseCase, context))
      .filter((chatUseCase): chatUseCase is ChatUseCase => chatUseCase != null),
  };
}

function resolveEnabledCategories(capabilities: ChatUseCaseCapabilities): Array<ChatUseCaseCategory> {
  const context: ChatUseCaseTenantContext = {isAcademicTenant: !BenchlingConfig.PRIVATE_DEPLOY};
  return CHAT_USE_CASE_CATEGORIES.filter((category) => category.isEnabled?.(capabilities) ?? true).map(
    (category) => resolveCategory(category, context)
  );
}

/**
 * The in-product listing of the catalog: featured use cases only, with empty categories dropped.
 *
 * URL lookup is broader and includes campaign-only prompts, so it does not go through here.
 */
export function withFeaturedChatUseCasesOnly(
  categories: Array<ChatUseCaseCategory>
): Array<ChatUseCaseCategory> {
  return categories
    .map((category) => ({
      ...category,
      chatUseCases: category.chatUseCases.filter((chatUseCase) => chatUseCase.featured),
    }))
    .filter((category) => category.chatUseCases.length > 0);
}

/** All featured categories visible in this tenant, in spec order. Used by the Use cases tab. */
export function getChatUseCaseCategories(capabilities: ChatUseCaseCapabilities): Array<ChatUseCaseCategory> {
  return withFeaturedChatUseCasesOnly(resolveEnabledCategories(capabilities));
}

/** A use case together with the category it sits in, which its run metric is reported against. */
export interface ChatUseCaseLookupResult {
  chatUseCase: ChatUseCase;
  categoryId: string;
}

/**
 * Resolve a single use case by its catalog id, for callers holding an id rather than a list: the
 * `use-case` URL parameter, and anything else that deep-links a specific prompt.
 *
 * A use case hidden in this tenant is indistinguishable from an id that was never defined. Both are
 * equally unreachable, and a caller has nothing different to offer for either. Featured is ignored:
 * a campaign-only prompt is still reachable from a link even though the tab does not list it.
 */
export function findChatUseCaseById(
  chatUseCaseId: string,
  capabilities: ChatUseCaseCapabilities
): ChatUseCaseLookupResult | null {
  for (const category of resolveEnabledCategories(capabilities)) {
    const chatUseCase = category.chatUseCases.find(({id}) => id === chatUseCaseId);
    if (chatUseCase != null) {
      return {chatUseCase, categoryId: category.id};
    }
  }
  return null;
}

/** The chat-workflow categories. Backs the existing progressive-disclosure selectors. */
export function getChatWorkflowCategories(): Array<ChatUseCaseCategory> {
  // No category in this subset gates on a caller-supplied capability, so the value is immaterial.
  const categories = withFeaturedChatUseCasesOnly(
    resolveEnabledCategories({isManageRoutineToolEnabled: false})
  );
  return CHAT_WORKFLOW_CATEGORY_IDS.map((categoryId) =>
    categories.find((category) => category.id === categoryId)
  ).filter((category): category is ChatUseCaseCategory => category != null);
}
